import React, { useEffect, useState, useRef, UIEvent } from 'react';
import { Modal, Button, Row, Col, Image, Alert, Form } from 'react-bootstrap';
import { useNavigate, useLocation } from 'react-router-dom';
import { Cnt } from '../Cnt.ThreeDeeCrate.SDK/Cnt.ThreeDeeCrate.SDK';

const styleNames = Object.keys(Cnt.ThreeDeeCrate.SDK.GraphicalStyleType).filter(k => isNaN(Number(k)));

export function StyleChooserModal() {
  const [show, setShow] = useState(false);
  const [scrolledToEnd, setScrolledToEnd] = useState(false);
  const [ackChecked, setAckChecked] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const scrollRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const segments = location.pathname.split('/').filter(Boolean);
    if (segments.length === 0) setShow(true);
  }, [location.pathname]);

  const handleChoose = (styleName: string) => {
    setShow(false);
    navigate(`/${encodeURIComponent(styleName)}/Root`, { replace: true });
  };

  const onScroll = (e: UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 4;
    if (atBottom && !scrolledToEnd) setScrolledToEnd(true);
  };

  const buttonsEnabled = scrolledToEnd && ackChecked;

  return (
    <Modal show={show} backdrop="static" centered>
      <Modal.Header>
        <Modal.Title>Choose Your Style</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {/* License / Disclaimer that must be scrolled */}
        <div className="mb-2 d-flex align-items-center justify-content-between">
          <strong>License & Disclaimer</strong>
          {!scrolledToEnd ? (
            <small className="text-muted">Scroll to the bottom to continue</small>
          ) : (
            <small className="text-success">Read complete</small>
          )}
        </div>

        <div
          ref={scrollRef}
          onScroll={onScroll}
          role="region"
          aria-label="License and disclaimer"
          tabIndex={0}
          style={{
            maxHeight: 220,
            overflowY: 'auto',
            padding: '0.75rem',
            border: '1px solid var(--bs-border-color, #dee2e6)',
            borderRadius: '0.5rem',
            background: 'var(--bs-body-bg)'
          }}
          className="mb-3"
        >
          <p><strong>Commercial Use Allowed.</strong> You may use the downloaded models in your own commercial
            projects and productions.</p>

          <p><strong>No Redistribution or Resale.</strong> You may <u>not</u> redistribute, share, publish, upload,
            or otherwise make these models available to others as standalone assets—paid or free. You may <u>not</u> resell,
            repackage, list, or include them in asset packs, marketplaces, libraries, or any product where the primary
            value is access to the raw models.</p>

          <p><strong>No “As-Is” Sharing.</strong> Do not provide direct access to the original files (or trivially
            modified versions) to any third party, including teammates outside your organization without appropriate
            seat licensing (if applicable).</p>

<p><strong>Project Use Only.</strong> You may include these models inside your own projects (like a game, film, render, or app). However, you cannot give other people direct access to the raw model files (e.g., .fbx, .obj, .glb). The models must be part of your finished product and not something others can open or re-use outside of it.</p>

          <p><strong>No Warranty.</strong> Assets are provided “as-is” without warranties. Visual differences may
            occur between thumbnails and final models due to generation angles, lighting, or pipeline updates.</p>

          <p><em>By checking the box below and proceeding, you confirm that you have read and agree to these terms.</em></p>
        </div>

        {/* Acknowledge checkbox (required after scrolling) */}
        <Form.Check
          type="checkbox"
          id="ack-terms"
          label="I have read and agree to the License & Disclaimer"
          checked={ackChecked}
          onChange={(e) => setAckChecked(e.target.checked)}
          disabled={!scrolledToEnd}
          className="mb-3"
        />

        {/* Style grid */}
        <Row className="text-center">
          {styleNames.map(name => (
            <Col key={name} className="mb-3">
              <Button
                variant="outline-warning"
                onClick={() => handleChoose(name)}
                className="w-100"
                disabled={!buttonsEnabled}
                aria-disabled={!buttonsEnabled}
                title={!buttonsEnabled ? 'Scroll the license and acknowledge to continue' : undefined}
              >
                <Image
                  src={`/icons/${name.toLowerCase()}.png`}
                  alt={name}
                  height={128}
                  className="mb-2 opacity-100"
                  style={{ filter: !buttonsEnabled ? 'grayscale(100%) opacity(70%)' : undefined }}
                />
                <br />
                {name}
              </Button>
            </Col>
          ))}
        </Row>

        <Alert variant="info" className="text-center mb-0">
          Commercial use permitted. <strong>No redistribution or resale.</strong>
        </Alert>
      </Modal.Body>
    </Modal>
  );
}
